import { Entity } from '@core/api/entity';

import { Field, Model, Serializer } from '@junte/serialize-ts';

import { FileObject, FileObjectSerializer } from './file-object.model';
import { ValueObject, ValueObjectSerializer } from './value-object.model';
import { ContactReadDTO, ContactWriteDTO } from '@core/dto/contact-copy.dto';

export class ContactSerializer implements Serializer<ContactCopy> {
  public serialize(model: ContactCopy): ContactWriteDTO {
    const vos = new ValueObjectSerializer();
    const dto: ContactWriteDTO = {
      uid: model.uid,
      id: model.id,
      address: model.address,
      firstName: model.firstName,
      lastName: model.lastName,
      email: model.email,
      isPep: model.isPep,
      title: model.title ? vos.serialize(model.title) : model.title,
      salutation: model.salutation ? vos.serialize(model.salutation) : model.salutation,
      type: model.type,
      idCardScan: model.idCardScan,
      signedDeclaration: model.signedDeclaration,
      phone: model.phone,
      dateOfBirth: model.dateOfBirth,
      percentageInCompany: model.percentageInCompany,
    };

    return Object.assign(dto) as ContactWriteDTO;
  }

  public deserialize(dto: ContactReadDTO): ContactCopy {
    const fos = new FileObjectSerializer();

    return ContactCopy.create({
      id: dto.id,
      uid: dto.uid,
      firstName: dto.firstName,
      lastName: dto.lastName,
      title: dto.title ? ValueObject.create(dto.title) : null,
      salutation: dto.salutation ? ValueObject.create(dto.salutation) : null,
      address: dto.address,
      isPep: dto.isPep,
      signedDeclaration: dto.signedDeclaration ? fos.deserialize(dto.signedDeclaration) : null,
      idCardScan: dto.idCardScan ? fos.deserialize(dto.idCardScan) : null,
      type: dto.type,
      email: dto.email,
      phone: dto.phone,
      dateOfBirth: dto.dateOfBirth as string,
      percentageInCompany: dto.percentageInCompany as number,
    });
  }
}

@Model(new ContactSerializer())
export class ContactCopy extends Entity {
  @Field({ serializer: new ContactSerializer() })
  id: string;
  @Field({ serializer: new ContactSerializer() })
  uid: string;
  @Field({ serializer: new ContactSerializer() })
  address: string;

  @Field({ serializer: new ContactSerializer() })
  email: string;

  @Field({ serializer: new ContactSerializer() })
  firstName: string;

  @Field({ serializer: new ContactSerializer() })
  isPep: boolean;

  @Field({ serializer: new ContactSerializer() })
  lastName: string;

  @Field({ serializer: new ContactSerializer() })
  phone: string;

  @Field({ serializer: new ContactSerializer() })
  salutation: ValueObject;

  @Field({ serializer: new ContactSerializer() })
  title: ValueObject;

  @Field({ serializer: new ContactSerializer() })
  type: string;
  @Field({ serializer: new ContactSerializer() })
  signedDeclaration: FileObject;
  @Field({ serializer: new ContactSerializer() })
  idCardScan: FileObject;

  @Field({ serializer: new ContactSerializer() })
  dateOfBirth: string;

  @Field({ serializer: new ContactSerializer() })
  percentageInCompany: number;

  public static create(data: Partial<ContactCopy>): ContactCopy {
    return Object.assign(new ContactCopy(), data);
  }

  public static createEmpty(): ContactCopy {
    return Object.assign(new ContactCopy(), {
      address: undefined,
      email: undefined,
      firstName: undefined,
      lastName: undefined,
      isPep: undefined,
      phone: undefined,
      idCardScan: undefined,
      signedDeclaration: undefined,
      title: undefined,
      salutation: undefined,
      type: undefined,
      dateOfBirth: undefined,
      percentageInCompany: undefined,
    });
  }
}
